import React, { useState, useEffect, useMemo } from 'react';
import { PrimaryLedgerCompany } from '@/types/primaryLedger';
import { primaryLedgerService } from '@/services/primaryLedgerService';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Database, RefreshCw } from 'lucide-react';
import CompanyCard from '@/components/primary-ledger/CompanyCard';
import AddCompanyForm from '@/components/primary-ledger/AddCompanyForm';
import SearchSortToolbar, { SortOption } from '@/components/primary-ledger/SearchSortToolbar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const ManageCompanies: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [companies, setCompanies] = useState<PrimaryLedgerCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingCompanyId, setDeletingCompanyId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');

  useEffect(() => {
    if (!user) return;

    const unsubscribe = primaryLedgerService.subscribeToCompanies(
      (updatedCompanies) => {
        setCompanies(updatedCompanies);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, toast]);

  const handleDeleteCompany = async () => {
    if (!deletingCompanyId) return;

    try {
      await primaryLedgerService.deleteCompany(deletingCompanyId);
      toast({
        title: 'Success',
        description: 'Company deleted successfully',
      });
      setDeletingCompanyId(null);
    } catch (error) {
      console.error('Error deleting company:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete company',
        variant: 'destructive',
      });
    }
  };

  const handleToggleSync = async (companyId: string, currentValue: boolean) => {
    try {
      await primaryLedgerService.toggleSyncEnabled(companyId, !currentValue);
      toast({
        title: 'Success',
        description: `Sync ${!currentValue ? 'enabled' : 'disabled'} successfully`,
      });
    } catch (error) {
      console.error('Error toggling sync:', error);
      toast({
        title: 'Error',
        description: 'Failed to toggle sync',
        variant: 'destructive',
      });
    }
  };

  const handleManualSync = async (companyId: string) => {
    try {
      await primaryLedgerService.triggerManualSync(companyId);
      toast({
        title: 'Success',
        description: 'Manual sync triggered successfully',
      });
    } catch (error) {
      console.error('Error triggering manual sync:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger manual sync',
        variant: 'destructive',
      });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const updatedCompanies = await primaryLedgerService.getAllCompanies();
      setCompanies(updatedCompanies);
      toast({
        title: 'Success',
        description: 'Companies refreshed',
      });
    } catch (error) {
      console.error('Error refreshing companies:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh companies',
        variant: 'destructive',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleCompanyAdded = () => {
    setShowAddForm(false);
  };

  // Filter and sort companies
  const filteredAndSortedCompanies = useMemo(() => {
    // Filter by search term
    const filtered = companies.filter(company =>
      company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort companies
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.companyName.localeCompare(b.companyName);
        case 'name-desc':
          return b.companyName.localeCompare(a.companyName);
        case 'sync-newest':
          return (b.lastSyncDate?.toMillis() || 0) - (a.lastSyncDate?.toMillis() || 0);
        case 'sync-oldest':
          return (a.lastSyncDate?.toMillis() || 0) - (b.lastSyncDate?.toMillis() || 0);
        case 'recently-added':
          return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0);
        case 'sync-status':
          return (b.syncEnabled ? 1 : 0) - (a.syncEnabled ? 1 : 0);
        default:
          return 0;
      }
    });

    return sorted;
  }, [companies, searchTerm, sortBy]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Manage Companies
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Configure Google Sheets connections for Primary Ledger companies
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowAddForm(!showAddForm)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      {showAddForm && (
        <AddCompanyForm onCompanyAdded={handleCompanyAdded} userId={user?.uid || ''} />
      )}

      {companies.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Database className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-center">
              No companies configured yet. Add your first company to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Search and Sort Toolbar */}
          <SearchSortToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            sortBy={sortBy}
            onSortChange={setSortBy}
            totalCount={companies.length}
            filteredCount={filteredAndSortedCompanies.length}
          />

          {/* Company Cards or Empty State */}
          {filteredAndSortedCompanies.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Database className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400 text-center">
                  No companies found matching "{searchTerm}"
                </p>
                <Button
                  onClick={() => setSearchTerm('')}
                  variant="outline"
                  size="sm"
                  className="mt-4"
                >
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredAndSortedCompanies.map((company) => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  onDelete={(id: string) => setDeletingCompanyId(id)}
                  onToggleSync={handleToggleSync}
                  onManualSync={handleManualSync}
                />
              ))}
            </div>
          )}
        </>
      )}

      <AlertDialog open={!!deletingCompanyId} onOpenChange={() => setDeletingCompanyId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this company? This action cannot be undone and will remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCompany}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManageCompanies;